/**
 * Ant Design theme configuration
 * Customizes Ant Design components to match DBS Portal design system
 */

import type { ThemeConfig } from 'antd'
import { colors } from '../tokens/colors'
import { fontFamily } from '../tokens/typography'

// Light theme configuration
export const lightTheme: ThemeConfig = {
  token: {
    // Color tokens
    colorPrimary: colors.primary[500],
    colorSuccess: colors.success[500],
    colorWarning: colors.warning[500],
    colorError: colors.error[500],
    colorInfo: colors.info[500],

    // Background colors
    colorBgBase: colors.white,
    colorBgContainer: colors.white,
    colorBgElevated: colors.white,
    colorBgLayout: colors.neutral[50],
    colorBgSpotlight: colors.neutral[100],
    colorBgMask: 'rgba(0, 0, 0, 0.45)',

    // Text colors
    colorText: colors.neutral[900],
    colorTextSecondary: colors.neutral[600],
    colorTextTertiary: colors.neutral[400],
    colorTextQuaternary: colors.neutral[300],

    // Border colors
    colorBorder: colors.neutral[200],
    colorBorderSecondary: colors.neutral[100],

    // Typography
    fontFamily: fontFamily.sans.join(', '),
    fontSize: 14, // Base font size
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,

    // Spacing
    padding: 16,
    paddingXS: 8,
    paddingSM: 12,
    paddingLG: 24,
    paddingXL: 32,

    margin: 16,
    marginXS: 8,
    marginSM: 12,
    marginLG: 24,
    marginXL: 32,

    // Border radius
    borderRadius: 6,
    borderRadiusXS: 2,
    borderRadiusSM: 4,
    borderRadiusLG: 8,

    // Shadows
    boxShadow:
      '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
    boxShadowSecondary:
      '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',

    // Line height
    lineHeight: 1.5714285714285714,
    lineHeightHeading1: 1.210526315789474,
    lineHeightHeading2: 1.2666666666666666,
    lineHeightHeading3: 1.3333333333333333,
    lineHeightHeading4: 1.4,
    lineHeightHeading5: 1.5,

    // Control heights
    controlHeight: 32,
    controlHeightSM: 24,
    controlHeightLG: 40,

    // Motion
    motionDurationFast: '0.1s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',
  },

  components: {
    // Button customization
    Button: {
      borderRadius: 6,
      controlHeight: 32,
      controlHeightSM: 24,
      controlHeightLG: 40,
      paddingInline: 15,
      paddingInlineSM: 7,
      paddingInlineLG: 15,
    },

    // Input customization
    Input: {
      borderRadius: 6,
      controlHeight: 32,
      controlHeightSM: 24,
      controlHeightLG: 40,
      paddingInline: 11,
      paddingInlineSM: 7,
      paddingInlineLG: 11,
    },

    // Card customization
    Card: {
      borderRadius: 8,
      paddingLG: 24,
      headerBg: 'transparent',
    },

    // Modal customization
    Modal: {
      borderRadius: 8,
      paddingLG: 24,
    },

    // Table customization
    Table: {
      borderRadius: 6,
      headerBg: colors.neutral[50],
      headerSplitColor: colors.neutral[200],
      rowHoverBg: colors.neutral[50],
    },

    // Menu customization
    Menu: {
      borderRadius: 6,
      itemBorderRadius: 6,
      subMenuItemBorderRadius: 6,
    },

    // Notification customization
    Notification: {
      borderRadius: 8,
    },

    // Message customization
    Message: {
      borderRadius: 6,
    },
  },
}

// Dark theme configuration
export const darkTheme: ThemeConfig = {
  ...lightTheme,
  token: {
    ...lightTheme.token,

    // Background colors for dark mode
    colorBgBase: colors.neutral[900],
    colorBgContainer: colors.neutral[800],
    colorBgElevated: colors.neutral[700],
    colorBgLayout: colors.neutral[950],
    colorBgSpotlight: colors.neutral[800],
    colorBgMask: 'rgba(0, 0, 0, 0.65)',

    // Text colors for dark mode
    colorText: colors.neutral[100],
    colorTextSecondary: colors.neutral[300],
    colorTextTertiary: colors.neutral[400],
    colorTextQuaternary: colors.neutral[500],

    // Border colors for dark mode
    colorBorder: colors.neutral[700],
    colorBorderSecondary: colors.neutral[800],
  },

  components: {
    ...lightTheme.components,

    // Table customization for dark mode
    Table: {
      ...lightTheme.components?.Table,
      headerBg: colors.neutral[800],
      headerSplitColor: colors.neutral[700],
      rowHoverBg: colors.neutral[800],
    },
  },
}

export type AntdTheme = typeof lightTheme
